import { BriefSocialMediaAiFitCheckStatus } from "@shared/lib/types/briefInfluencerV2";
import { Table } from "@shared/ui";
import { Checkbox } from "@shared/ui/Checkbox";
import NumberRangeFilter from "@shared/ui/filters/NumberRangeFilter";
import { AiMatchDisplay } from "@widgets/CampaignBrief/ui/CampaignBriefTable/AiMatchCell";
import React, { isValidElement, useMemo, useState } from "react";
import {
  CreatorRosterIdentity,
  FollowerCount,
  creatorRosterScroll,
  readFollowerCount,
} from "../CreatorRosterAdd";
import rosterStyles from "../CreatorRosterAdd/styles.module.scss";
import pageStyles from "../styles.module.scss";
import { CreatorFitCheck, getFitScore } from "./exportCreatorFitExcel";

export function isCheckAnalyzing(
  check: CreatorFitCheck,
  reanalyzingId: string | null,
  isReanalyzing: boolean
) {
  const status = check.briefFitEvaluationJob?.status as string | undefined;
  return (
    (isReanalyzing && reanalyzingId === check._id) ||
    status === BriefSocialMediaAiFitCheckStatus.Evaluating ||
    status === BriefSocialMediaAiFitCheckStatus.WaitingCreatorProfile ||
    status === BriefSocialMediaAiFitCheckStatus.WaitingPrerequisites ||
    status === "pending" ||
    status === "in_progress" ||
    status === "processing" ||
    (!check.briefFitEvaluationJob && Boolean(check.briefFitEvaluationJobId))
  );
}

export function CreatorFitTable({
  checks,
  requestMasterPromptId,
  promptChanged,
  reanalyzingId,
  isReanalyzing,
  onReanalyze,
  selectedRowKeys,
  onSelectChange,
}: {
  checks: CreatorFitCheck[];
  requestMasterPromptId?: string;
  promptChanged: boolean;
  reanalyzingId: string | null;
  isReanalyzing: boolean;
  onReanalyze: (checkId: string) => void;
  selectedRowKeys?: React.Key[];
  onSelectChange?: (keys: React.Key[]) => void;
}) {
  const [fitScoreRange, setFitScoreRange] = useState<{
    from?: number;
    to?: number;
  }>({});
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | undefined>(
    undefined
  );

  const filteredChecks = useMemo(() => {
    let result = [...checks];

    if (fitScoreRange.from !== undefined || fitScoreRange.to !== undefined) {
      result = result.filter((check) => {
        const score = getFitScore(check);
        if (score === undefined) return false;
        if (fitScoreRange.from !== undefined && score < fitScoreRange.from)
          return false;
        if (fitScoreRange.to !== undefined && score > fitScoreRange.to)
          return false;
        return true;
      });
    }

    if (sortOrder) {
      result.sort((a, b) => {
        const scoreA = getFitScore(a);
        const scoreB = getFitScore(b);
        if (scoreA === undefined && scoreB === undefined) return 0;
        if (scoreA === undefined) return 1;
        if (scoreB === undefined) return -1;
        return sortOrder === "asc" ? scoreA - scoreB : scoreB - scoreA;
      });
    }

    return result;
  }, [checks, fitScoreRange, sortOrder]);

  const isRangeFiltered =
    fitScoreRange.from !== undefined || fitScoreRange.to !== undefined;
  const isSorted = Boolean(sortOrder);
  const filterCount = (isRangeFiltered ? 1 : 0) + (isSorted ? 1 : 0);

  return (
    <Table<CreatorFitCheck>
      wrapperClassName={rosterStyles.tableWrap}
      pagination={false}
      scroll={creatorRosterScroll}
      size="small"
      rowKey="_id"
      dataSource={filteredChecks}
      rowSelection={
        onSelectChange
          ? {
              selectedRowKeys: selectedRowKeys || [],
              columnWidth: 40,
              onChange: onSelectChange,
              renderCell: (_checked, _record, _index, originNode) => {
                const checkboxProps = isValidElement(originNode)
                  ? (originNode.props as Record<string, unknown>)
                  : {};

                return <Checkbox {...checkboxProps} iconSize={18} />;
              },
              columnTitle: (checkboxNode) => {
                if (isValidElement(checkboxNode)) {
                  return (
                    <Checkbox
                      {...(checkboxNode.props as Record<string, unknown>)}
                      iconSize={18}
                    />
                  );
                }
                return checkboxNode;
              },
            }
          : undefined
      }
      columns={[
        {
          title: "Creator",
          key: "creator",
          render: (_, check) => {
            const socialMedia = check.socialMedia;
            const name =
              socialMedia?.name || socialMedia?.username || "Unknown Creator";
            const checkPromptId =
              check.briefFitEvaluationJob?.aiWorkspaceMasterPromptId;
            const outdated =
              Boolean(check.briefFitEvaluationJob) &&
              (promptChanged ||
                (Boolean(requestMasterPromptId) &&
                  Boolean(checkPromptId) &&
                  requestMasterPromptId !== checkPromptId));
            const analyzing = isCheckAnalyzing(
              check,
              reanalyzingId,
              isReanalyzing
            );
            return (
              <span className={rosterStyles.identity}>
                <CreatorRosterIdentity
                  name={name}
                  image={socialMedia?.profilePicUrl}
                  platform={socialMedia?.platform}
                  username={socialMedia?.username}
                  linkProfile
                />
                {outdated && !analyzing ? (
                  <span className={pageStyles.outdatedBadge}>Outdated</span>
                ) : null}
              </span>
            );
          },
        },
        {
          title: "Followers",
          key: "followers",
          width: 96,
          render: (_, check) => (
            <FollowerCount count={readFollowerCount(check.socialMedia)} />
          ),
        },
        {
          title: "Fit score",
          key: "fit",
          width: 140,
          filterCount,
          filtered: filterCount > 0,
          filterDropdownProps: {
            destroyPopupOnHide: true,
          },
          filterDropdown: (filterProps) => (
            <NumberRangeFilter
              title="Fit score"
              placeholder="Enter score range"
              selectedRange={fitScoreRange}
              filterCount={filterCount}
              handleRangeChange={(range) => {
                setFitScoreRange(range);
              }}
              props={filterProps}
              sortActive={isSorted}
              sortDirection={sortOrder}
              sortAscLabel="Low to High"
              sortDescLabel="High to Low"
              onSortChange={(dir) => {
                setSortOrder(dir);
              }}
              onReset={() => {
                setFitScoreRange({});
                setSortOrder(undefined);
              }}
            />
          ),
          render: (_, check) => {
            const isAnalyzing = isCheckAnalyzing(
              check,
              reanalyzingId,
              isReanalyzing
            );
            return (
              <AiMatchDisplay
                fitCheck={check.briefFitEvaluationJob}
                isAnalyzing={isAnalyzing}
                onAnalyze={() => {
                  if (check._id) onReanalyze(check._id);
                }}
              />
            );
          },
        },
      ]}
    />
  );
}

